import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { refreshSettings } from "@/hooks/useSettings";
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Upload, Plus, Trash2, Image as ImageIcon } from "lucide-react";

type SocialLinks = {
  facebook: string;
  twitter: string;
  instagram: string;
  linkedin: string;
  youtube: string;
};

type FooterLink = { label: string; url: string };
type FooterColumn = { heading: string; links: FooterLink[] };

const SOCIAL_FIELDS: { key: keyof SocialLinks; label: string; icon: any; placeholder: string }[] = [
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/yourpage" },
  { key: "twitter", label: "Twitter / X", icon: Twitter, placeholder: "https://x.com/yourhandle" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/yourhandle" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/company/..." },
  { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@yourchannel" },
];

const BASIC_KEYS = [
  { key: "store_name", label: "Store name", type: "text" },
  { key: "currency_symbol", label: "Currency symbol", type: "text" },
  { key: "contact_email", label: "Contact email", type: "email" },
  { key: "hero_title", label: "Hero title", type: "text" },
  { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
  { key: "hero_cta_text", label: "Hero CTA text", type: "text" },
];

const AdminSettings = () => {
  const [basic, setBasic] = useState<Record<string, string>>({});
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [social, setSocial] = useState<SocialLinks>({
    facebook: "", twitter: "", instagram: "", linkedin: "", youtube: "",
  });
  const [footerAbout, setFooterAbout] = useState("");
  const [columns, setColumns] = useState<FooterColumn[]>([]);
  const [copyright, setCopyright] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    supabase.from("settings").select("key, value").then(({ data }) => {
      const map: Record<string, any> = {};
      data?.forEach((r) => { map[r.key] = r.value; });
      const b: Record<string, string> = {};
      BASIC_KEYS.forEach((k) => {
        b[k.key] = typeof map[k.key] === "string" ? map[k.key] : (map[k.key] ?? "");
      });
      setBasic(b);
      setLogoUrl(typeof map.logo_url === "string" ? map.logo_url : "");
      if (map.social_links && typeof map.social_links === "object") {
        setSocial({ ...social, ...map.social_links });
      }
      setFooterAbout(typeof map.footer_about === "string" ? map.footer_about : "");
      setCopyright(typeof map.copyright_text === "string" ? map.copyright_text : "");
      if (Array.isArray(map.footer_columns)) setColumns(map.footer_columns);
      else setColumns([{ heading: "Shop", links: [{ label: "", url: "" }] }]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPickLogo = (file: File | null) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|svg\+xml|webp)$/.test(file.type)) {
      toast.error("Please upload PNG, JPG, SVG, or WebP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogoIfNeeded = async (): Promise<string> => {
    if (!logoFile) return logoUrl;
    const ext = logoFile.name.split(".").pop()?.toLowerCase() || "png";
    const path = `logo-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("site-assets").upload(path, logoFile, {
      cacheControl: "3600",
      upsert: true,
      contentType: logoFile.type,
    });
    if (error) throw error;
    const { data } = supabase.storage.from("site-assets").getPublicUrl(path);
    return data.publicUrl;
  };

  const addColumn = () =>
    setColumns([...columns, { heading: "New column", links: [{ label: "", url: "" }] }]);
  const removeColumn = (i: number) => setColumns(columns.filter((_, idx) => idx !== i));
  const updateColumn = (i: number, patch: Partial<FooterColumn>) =>
    setColumns(columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addLink = (i: number) =>
    updateColumn(i, { links: [...columns[i].links, { label: "", url: "" }] });
  const updateLink = (i: number, j: number, patch: Partial<FooterLink>) =>
    updateColumn(i, { links: columns[i].links.map((l, idx) => (idx === j ? { ...l, ...patch } : l)) });
  const removeLink = (i: number, j: number) =>
    updateColumn(i, { links: columns[i].links.filter((_, idx) => idx !== j) });

  const save = async () => {
    setSaving(true);
    try {
      const finalLogo = await uploadLogoIfNeeded();
      const rows: { key: string; value: any }[] = [
        ...BASIC_KEYS.map((k) => ({ key: k.key, value: basic[k.key] ?? "" })),
        { key: "logo_url", value: finalLogo },
        { key: "social_links", value: social },
        { key: "footer_about", value: footerAbout },
        { key: "footer_columns", value: columns.map((c) => ({
          heading: c.heading,
          links: c.links.filter((l) => l.label.trim() && l.url.trim()),
        })) },
        { key: "copyright_text", value: copyright },
      ];
      const { error } = await supabase.from("settings").upsert(rows);
      if (error) throw error;
      setLogoUrl(finalLogo);
      setLogoFile(null);
      setLogoPreview(null);
      toast.success("Settings saved successfully");
      refreshSettings();
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold">Site settings</h1>
        <p className="text-muted-foreground">Customize your storefront branding, social presence, and footer</p>
      </div>

      {/* Basic */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">General</h2>
        {BASIC_KEYS.map((k) => (
          <div key={k.key}>
            <Label htmlFor={k.key}>{k.label}</Label>
            {k.type === "textarea" ? (
              <Textarea id={k.key} rows={3} value={basic[k.key] || ""} onChange={(e) => setBasic({ ...basic, [k.key]: e.target.value })} />
            ) : (
              <Input id={k.key} type={k.type} value={basic[k.key] || ""} onChange={(e) => setBasic({ ...basic, [k.key]: e.target.value })} />
            )}
          </div>
        ))}
      </Card>

      {/* Logo */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Website logo</h2>
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-2xl border-2 border-dashed border-border bg-muted/40 grid place-items-center overflow-hidden shrink-0">
            {logoPreview || logoUrl ? (
              <img src={logoPreview || logoUrl} alt="Logo preview" className="h-full w-full object-contain" />
            ) : (
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={(e) => onPickLogo(e.target.files?.[0] ?? null)}
            />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Choose logo
            </Button>
            <p className="text-xs text-muted-foreground">PNG, JPG, SVG or WebP. Max 2 MB.</p>
            {logoFile && <p className="text-xs text-foreground">Ready to upload: {logoFile.name}</p>}
          </div>
        </div>
      </Card>

      {/* Social links */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Social links</h2>
        <div className="grid gap-3">
          {SOCIAL_FIELDS.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="flex items-center gap-3">
                <div className="h-10 w-10 grid place-items-center rounded-lg bg-muted shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <Label htmlFor={`social-${f.key}`} className="sr-only">{f.label}</Label>
                  <Input
                    id={`social-${f.key}`}
                    placeholder={f.placeholder}
                    value={social[f.key]}
                    onChange={(e) => setSocial({ ...social, [f.key]: e.target.value })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Footer */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Footer content</h2>
        <div>
          <Label htmlFor="footer-about">About / description</Label>
          <Textarea
            id="footer-about"
            rows={3}
            value={footerAbout}
            onChange={(e) => setFooterAbout(e.target.value)}
            placeholder="A short description shown in the footer..."
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Footer columns</Label>
            <Button type="button" variant="outline" size="sm" onClick={addColumn}>
              <Plus className="h-3 w-3" /> Add column
            </Button>
          </div>

          {columns.map((col, i) => (
            <div key={i} className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  value={col.heading}
                  onChange={(e) => updateColumn(i, { heading: e.target.value })}
                  placeholder="Column heading"
                  className="font-medium"
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeColumn(i)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2 pl-1">
                {col.links.map((link, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Input
                      value={link.label}
                      placeholder="Label"
                      onChange={(e) => updateLink(i, j, { label: e.target.value })}
                    />
                    <Input
                      value={link.url}
                      placeholder="URL (e.g. /orders or https://...)"
                      onChange={(e) => updateLink(i, j, { url: e.target.value })}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeLink(i, j)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="ghost" size="sm" onClick={() => addLink(i)}>
                  <Plus className="h-3 w-3" /> Add link
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Copyright */}
      <Card className="p-6 border-0 shadow-soft space-y-4">
        <h2 className="font-semibold text-lg">Copyright</h2>
        <div>
          <Label htmlFor="copyright">Copyright text</Label>
          <Input
            id="copyright"
            value={copyright}
            onChange={(e) => setCopyright(e.target.value)}
            placeholder="© 2026 MyBrand. All rights reserved."
          />
        </div>
      </Card>

      <div className="sticky bottom-4 z-10">
        <Button variant="hero" size="lg" onClick={save} disabled={saving} className="w-full sm:w-auto">
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
